import type { FormFieldDef } from '@hcc/types';
import { inputClass } from './fieldStyles';
import { FieldLabelText, FieldHelp, INVALID_GROUP_CLASS } from './fieldChrome';
import { asString, asArray, type FieldValue } from './formValues';

interface OptionFieldViewProps {
  field: FormFieldDef;
  value: FieldValue;
  onChange: (next: FieldValue) => void;
  disabled?: boolean;
  err: boolean;
}

/**
 * The options-based input family: dropdown, radio (incl. the implicit
 * yes_no pair) and multi-select checkbox.
 */
export function OptionFieldView({ field, value, onChange, disabled, err }: OptionFieldViewProps) {
  if (field.type === 'dropdown') {
    return (
      <label className="block text-sm">
        <FieldLabelText field={field} />
        <select
          value={asString(value)}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass(err)}
        >
          <option value="">Please choose…</option>
          {(field.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <FieldHelp field={field} />
      </label>
    );
  }

  if (field.type === 'checkbox') {
    const selected = asArray(value);
    function toggle(v: string) {
      onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
    }
    return (
      <fieldset className="block text-sm">
        <FieldLabelText field={field} />
        <div className={`mt-1.5 flex flex-col gap-1.5 ${err ? INVALID_GROUP_CLASS : ''}`}>
          {(field.options ?? []).map((o) => (
            <label key={o.value} className="flex items-center gap-1.5 text-sm text-fg">
              <input
                type="checkbox"
                disabled={disabled}
                checked={selected.includes(o.value)}
                onChange={() => toggle(o.value)}
              />
              {o.label}
            </label>
          ))}
        </div>
        <FieldHelp field={field} />
      </fieldset>
    );
  }

  // radio / yes_no
  const options =
    field.type === 'yes_no'
      ? [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ]
      : (field.options ?? []);
  return (
    <fieldset className="block text-sm">
      <FieldLabelText field={field} />
      <div className={`mt-1.5 flex flex-wrap gap-3 ${err ? INVALID_GROUP_CLASS : ''}`}>
        {options.map((o) => (
          <label key={o.value} className="flex items-center gap-1.5 text-sm text-fg">
            <input
              type="radio"
              name={field.key}
              value={o.value}
              disabled={disabled}
              checked={asString(value) === o.value}
              onChange={() => onChange(o.value)}
            />
            {o.label}
          </label>
        ))}
      </div>
      <FieldHelp field={field} />
    </fieldset>
  );
}
